import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Slider from 'react-slick';
import { useAllCardsStore, useCardsLatestStore, useCardsProductsStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { cn, LocalStorageUtils, RoutePaths } from '@shared/utils';
import type { GetAllCardsResponse } from '@shared/api';
import { Card, CardInfo, Invoices, MobileCardInfo, Statements } from './widgets';
import { PageLayout, Preloader } from '@shared/widgets';
import styles from './cards.module.css';

const settings = { focusOnSelect: true, dots: false, infinite: false, speed: 500, variableWidth: true, slidesToScroll: 1, slidesToShow: 1 };

export const CardsPage = () => {
  const location = useLocation();
  const { navigate } = useRouteHook();

  const allCardsStore = useAllCardsStore();
  const cardsLatestStore = useCardsLatestStore();
  const cardsProductsStore = useCardsProductsStore();

  const [selectedCard, setSelectedCard] = useState<GetAllCardsResponse>();
  const [currentCard, setCurrentCard] = useState<number>(0);
  const [mobileTab, setMobileTab] = useState<string>('');
  const sliderRef = useRef<Slider>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (location.state?.scrollToCardId) {
      const cardId = location.state.scrollToCardId;

      const checkAndScroll = () => {
        if (allCardsStore.data && cardRefs.current[cardId]) {
          cardRefs.current[cardId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          const cardIndex = allCardsStore.data.findIndex((card) => card.id === cardId);
          if (cardIndex !== -1 && sliderRef.current) {
            sliderRef.current.slickGoTo(cardIndex);
            setCurrentCard(cardIndex);
          }
        } else {
          setTimeout(checkAndScroll, 100);
        }
      };

      checkAndScroll();
    }
  }, [location.state, allCardsStore.data]);

  useEffect(() => {
    allCardsStore.get();
    cardsLatestStore.get();

    if (cardsProductsStore.data?.length === 0) cardsProductsStore.get();
    if (location.state?.item) setSelectedCard(location.state.item);

    if (location.state?.selectedCard) {
      setSelectedCard(location.state.selectedCard);
      if (location.state.selectedCard.id && allCardsStore.data) {
        const cardIndex = allCardsStore.data.findIndex((card) => card.id === location.state.selectedCard.id);
        if (cardIndex !== -1) {
          setCurrentCard(cardIndex);
          if (sliderRef.current) sliderRef.current.slickGoTo(cardIndex);
        }
      }
    }
  }, []);

  const manageCardHandler = (card: GetAllCardsResponse, index: number) => {
    LocalStorageUtils.setSelectedCardIndex(index);
    LocalStorageUtils.setSelectedCard(card);
    navigate(RoutePaths.manageCards.onNavigate(), { state: { item: card, index, cardOptions: true } });
  };

  return (
    <PageLayout>
      {!allCardsStore.data && <Preloader />}
      {allCardsStore.data && (
        <div className='flex flex-col items-center justify-center bg-[#fafafa] pt-25 text-[#4c4c4c] max-md:block max-md:pt-0'>
          {/* MOBILE */}
          <div className='max-md:inherit hidden'>
            {mobileTab == '' && (
              <div className='bg-[#d7c29a]'>
                <div className={cn('relative overflow-hidden pt-8 pb-13', styles['cards-list-mobile-list'])}>
                  <Slider {...settings} ref={sliderRef} beforeChange={(_, nextSlide) => setCurrentCard(nextSlide)}>
                    {allCardsStore.data.map((card, idx) => (
                      <div key={idx} className='pl-2'>
                        <div className='bg-contain bg-center bg-no-repeat' style={{ width: 'calc(100vw - 54px)' }}>
                          <Card card={card} />
                        </div>
                      </div>
                    ))}
                  </Slider>
                  <ul className='absolute bottom-6.25 left-1/2 mx-auto flex -translate-x-1/2 justify-center gap-2 p-0'>
                    {allCardsStore.data.map((_, idx) => (
                      <li key={idx} className={cn('m-0 h-2 w-2 rounded-full bg-[#4c4c4c80] p-0', currentCard === idx && 'bg-[#0f0f11]')} />
                    ))}
                  </ul>
                </div>
                <MobileCardInfo
                  card={allCardsStore.data?.[currentCard] || allCardsStore.data?.[0]}
                  index={currentCard}
                  onManageCard={manageCardHandler}
                  onChangeTab={(tab) => setMobileTab(tab)}
                />
              </div>
            )}
            {mobileTab === 'invoices' && <Invoices card={allCardsStore.data?.[currentCard] || allCardsStore.data?.[0]} />}
            {mobileTab === 'statements' && <Statements card={allCardsStore.data?.[currentCard] || allCardsStore.data?.[0]} />}
          </div>
          {/* DESKTOP */}
          <div className='max-md:hidden'>
            {allCardsStore.data.map((card, idx) => (
              <div
                key={card.id}
                ref={(el) => {
                  if (el) {
                    cardRefs.current[card.id] = el;
                  }
                }}
              >
                <CardInfo
                  key={idx}
                  card={card}
                  index={idx}
                  selectedCard={selectedCard}
                  onClick={() => setSelectedCard((prev) => (prev ? undefined : card))}
                  onManageCard={manageCardHandler}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </PageLayout>
  );
};
